/**
 * Time Machine #114 Phase 6 — pure helper that computes a structured diff
 * between two schedule states (left T1, right T2) plus the events that
 * occurred between those moments.
 *
 * Output shape feeds the Diff Pair grid UI directly: each entry identifies
 * one job or attribute that differs between left and right, the kind of
 * change (added / removed / changed), and the chronological list of events
 * that touched it. The frontend renders one stacked bubble per event with
 * the changedBy username (domain stripped).
 *
 * Inputs:
 *   leftProjects     — array of ActiveQueue docs at T1
 *   rightProjects    — array of ActiveQueue docs at T2
 *   leftAttributes   — array of Attributes docs at T1
 *   rightAttributes  — array of Attributes docs at T2
 *   eventsBetween    — array of EventLog entries with T1 < timestamp <= T2,
 *                      both ActiveQueue and Attributes events
 *
 * Output:
 *   {
 *     projectDiffs: [...],
 *     attributeDiffs: [...]
 *   }
 *
 * Each diff entry:
 *   {
 *     id,                  // doc _id (string)
 *     changeKind,          // "added" | "removed" | "changed"
 *     leftDoc,             // doc at T1 (undefined if added)
 *     rightDoc,            // doc at T2 (undefined if removed)
 *     events: [...],       // sorted by timestamp ascending; events whose
 *                          // documentId === this id and collection matches
 *   }
 *
 * Pure / side-effect-free / deterministic.
 */

const NOISE_FIELDS = new Set([
  "_id",
  "_lastChangedAt",
  "_lastChangedBy",
  "_changeSource",
  "history",
  "lastModifiedTimestamp",
  "lastUpdated",
  "updatedAt",
  "modifiedAt",
  "modificationTS",
  "modificationTS_invoiceRecord",
  "modCount",
]);

export function computeScheduleDiff({
  leftProjects = [],
  rightProjects = [],
  leftAttributes = [],
  rightAttributes = [],
  eventsBetween = [],
} = {}) {
  return {
    projectDiffs: diffCollection(
      leftProjects,
      rightProjects,
      eventsBetween,
      "ActiveQueue"
    ),
    attributeDiffs: diffCollection(
      leftAttributes,
      rightAttributes,
      eventsBetween,
      "Attributes"
    ),
  };
}

/**
 * Compute diffs for one collection. For each doc id that exists on either
 * side, classify as added / removed / changed based on shallow content
 * equality. Then attach all events from the window that touched that doc.
 */
function diffCollection(leftDocs, rightDocs, events, collection) {
  const leftMap = buildMap(leftDocs);
  const rightMap = buildMap(rightDocs);
  const eventsByDocId = bucketEventsByDocId(events, collection);

  const ids = new Set([...leftMap.keys(), ...rightMap.keys()]);
  const diffs = [];
  for (const id of ids) {
    const leftDoc = leftMap.get(id);
    const rightDoc = rightMap.get(id);
    if (leftDoc && !rightDoc) {
      diffs.push({
        id,
        changeKind: "removed",
        leftDoc,
        rightDoc: undefined,
        events: eventsByDocId.get(id) || [],
      });
    } else if (!leftDoc && rightDoc) {
      diffs.push({
        id,
        changeKind: "added",
        leftDoc: undefined,
        rightDoc,
        events: eventsByDocId.get(id) || [],
      });
    } else if (leftDoc && rightDoc && !docsEquivalent(leftDoc, rightDoc)) {
      diffs.push({
        id,
        changeKind: "changed",
        leftDoc,
        rightDoc,
        events: eventsByDocId.get(id) || [],
      });
    }
    // else: docs match — no diff entry
  }
  return diffs;
}

function buildMap(docs) {
  const m = new Map();
  for (const doc of docs) {
    if (!doc) continue;
    const id = doc?._id?.toString?.() ?? doc?._id;
    if (id != null) m.set(String(id), doc);
  }
  return m;
}

function bucketEventsByDocId(events, collection) {
  const m = new Map();
  for (const ev of events) {
    if (ev?.collection !== collection) continue;
    const id = ev.documentId?.toString?.() ?? ev.documentId;
    if (id == null) continue;
    const key = String(id);
    const list = m.get(key) || [];
    list.push(ev);
    m.set(key, list);
  }
  // Sort each bucket ascending by timestamp (chronological order of changes
  // — bubble stacks read top-to-bottom as oldest-to-newest in the window).
  for (const list of m.values()) {
    list.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      if (ta !== tb) return ta - tb;
      return String(a._id).localeCompare(String(b._id));
    });
  }
  return m;
}

/**
 * Two docs are equivalent for diff purposes if they match on all non-noise
 * fields. Noise fields (timestamps, source-attribution, FileMaker auto-update
 * fields) change on every write but don't represent business intent.
 *
 * Uses JSON-equal as a deep-equal proxy. Good enough for "did this doc
 * change in a way the user cares about?" — not worth pulling in lodash.
 */
function docsEquivalent(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;

  const stripped = (doc) => {
    const out = {};
    for (const key of Object.keys(doc)) {
      if (NOISE_FIELDS.has(key)) continue;
      out[key] = doc[key];
    }
    return out;
  };
  try {
    return JSON.stringify(stripped(a)) === JSON.stringify(stripped(b));
  } catch {
    return false;
  }
}

/**
 * Strip "@domain.com" from a username string. All employees are in the same
 * email domain, so "alice@kellerinteriors.com" reads better as "alice".
 * Non-string inputs and strings without "@" are returned unchanged.
 */
export function stripUsernameDomain(username) {
  if (typeof username !== "string") return username;
  const at = username.indexOf("@");
  if (at < 0) return username;
  return username.slice(0, at);
}

/**
 * Resolve the most useful user-attribution string for a given EventLog entry.
 *
 * The Lambda stamps `changedBy` from the Scheduler-UI mutation pipeline
 * (Phase 1). When a write originates outside that pipeline (FileMaker via
 * Atlas Data API, dbtools scripts, direct DB writes), there's no logged-in
 * Scheduler user to attribute and `changedBy` falls back to "unknown".
 *
 * FileMaker, however, stamps its OWN user-attribution into the document
 * itself. The exact field name DIFFERS BY COLLECTION (verified against PROD
 * EventLog 2026-05-08):
 *   - ActiveQueue uses `_modifitionAcct` (note the typo: missing 'a' and
 *     abbreviated "Acct") and `_creationAcct`.
 *   - Candidates uses `_modificationAccount` and `_creationAccount`.
 *   - Attributes is NEVER written by FileMaker (Scheduler-UI only) — so
 *     when an Attributes event has changedBy="unknown" it's a Scheduler-side
 *     user-capture issue, NOT a FileMaker write. No FM fallback helps.
 *
 * Resolution order (after exhausting changedBy):
 *   1. after._modifitionAcct       (ActiveQueue FM update — most common FM event)
 *   2. after._modificationAccount  (Candidates FM update)
 *   3. after._creationAcct         (ActiveQueue FM create — _modifitionAcct may be empty)
 *   4. after._creationAccount      (Candidates FM create)
 *   5. before._modifitionAcct      (ActiveQueue FM delete — best-effort)
 *   6. before._modificationAccount (Candidates FM delete — best-effort)
 *   7. "unknown" final fallback
 *
 * Note on delete fallback: `before._modifitionAcct` is the LAST FM user who
 * touched the doc PRIOR to the delete. The actual deleter may differ but is
 * usually the same in practice; this is closer than "unknown".
 */
export function getEffectiveChangedBy(event) {
  if (!event || typeof event !== "object") return "unknown";
  const cb = event.changedBy;
  if (typeof cb === "string" && cb !== "" && cb !== "unknown") {
    return cb;
  }
  const after = event.after && typeof event.after === "object" ? event.after : null;
  const before = event.before && typeof event.before === "object" ? event.before : null;
  const candidates = [
    after?._modifitionAcct,
    after?._modificationAccount,
    after?._creationAcct,
    after?._creationAccount,
    before?._modifitionAcct,
    before?._modificationAccount,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c !== "") return c;
  }
  return "unknown";
}

export const __testables__ = { NOISE_FIELDS, docsEquivalent, buildMap };
